import React, { useContext, useMemo, useReducer } from 'react';
import { SNACKBAR_SEVERITY_LEVEL } from './StringConstants';

/**
 * This file contains all items related to storing the state of the Snackbar in a global sense (context)
 * along with ways to mutate it.
 * 
 * 1. SnackbarProvider is used in FoldersPageContainer.jsx, wrapping out application
 * 2. A custom hook (useSnackbar) is used by consumers. This provides a clean API to mutate the state of the Snackbar.
 * 3. A reducer is used to hold and mutate state. This value is stored in the SnackbarContext (served by the Provider).
 *
 * @param {*} state The previous state
 * @param {*} action The action to determine the proper reduction of state
 * @returns The new state of the Snackbar
 */

const initialSnackbarState = {
    isOpen: false,
    message: "",
    severity: SNACKBAR_SEVERITY_LEVEL.SUCCESS
};

const SnackbarContext = React.createContext();

/**
 * Reducer function for mutating the state of the Snackbar. 
 *
 * @param {*} state The previous state
 * @param {*} action The action to determine the proper reduction of state
 * @returns The new state of the Snackbar
 */
function snackbarReducer(state, action) {
    switch (action.type) {
        case 'SHOW_SUCCESS_SNACKBAR':
            return {
                isOpen: true,
                message: action.message,
                severity: action.severity
            }
        case 'SHOW_ERROR_SNACKBAR':
            return {
                isOpen: true,
                message: action.message,
                severity: action.severity
            }
        case 'HIDE_SNACKBAR':
            return {
                isOpen: false,
                message: state.message,
                severity: state.severity
            }
        default:
            return state;
    }
}


/**
 * Custom hook for showing Snackbar alert messages throughout our application. 
 * Usage example: const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
 * 
 * Both show functions accept a message to display.
 * @returns {Object} Returns an object that contains functions to trigger the display of a success or error
 * Snackbar, as well as a function to hide the Snackbar if needed.
 * 
 * showSuccessSnackbar(message): A function which accepts a message to display in the Success Snackbar.
 * showErrorSnackbar(message): A function which accepts a message to display in the Error Snackbar.
 */
function useSnackbar() {
    const context = useContext(SnackbarContext);

    if (!context) {
        throw new Error(`useSnackbar must be used within a SnackbarProvider!`);
    }
    const [state, dispatch] = context;

    const showSuccessSnackbar = (message) => dispatch({
        type: 'SHOW_SUCCESS_SNACKBAR',
        message: message,
        severity: SNACKBAR_SEVERITY_LEVEL.SUCCESS
    });

    const showErrorSnackbar = (message) => dispatch({
        type: 'SHOW_ERROR_SNACKBAR',
        message: message,
        severity: SNACKBAR_SEVERITY_LEVEL.ERROR
    });

    const hideSnackbar = () => dispatch({ type: 'HIDE_SNACKBAR' });

    return { state, showSuccessSnackbar, showErrorSnackbar, hideSnackbar };
}


/**
 * The context provider which stores the Snackbar state. We wrap this around our app at the top level 
 * of our render tree in order to allow any child component to have access to the provider.
 *
 * @param {*} props
 * @returns
 */
function SnackbarProvider(props) {
    const [state, dispatch] = useReducer(snackbarReducer, initialSnackbarState);
    const value = useMemo(() => [state, dispatch], [state]);

    return <SnackbarContext.Provider value={value} {...props} />;
}

export { SnackbarProvider, useSnackbar };

